import { Injectable } from "@angular/core";

import { OfferPlanCode } from "../../shared/types/offer-upsell";
import {
  Organization,
  SubscriptionPlanCatalogItem,
  SubscriptionPlanVersion,
} from "../models/organizations.entity";
import { SubscriptionPlanMetadata } from "../models/subscription-plan.metadata";
import {
  getAnnualPricePerSeatCentsFromCapabilities,
  resolvePlanVersionForCatalogPricing,
} from "../utils/plan-pricing.util";

@Injectable({ providedIn: "root" })
export class ManagedOfferCheckoutService {
  private catalogBySlug: Record<string, SubscriptionPlanCatalogItem> = {};

  setCatalog(plans: SubscriptionPlanCatalogItem[]): void {
    this.catalogBySlug = plans.reduce((accumulator, plan) => {
      accumulator[plan.slug] = plan;
      return accumulator;
    }, {} as Record<string, SubscriptionPlanCatalogItem>);
  }

  getManagedPlanCodes(): OfferPlanCode[] {
    const enterpriseSlugs = [ "wetalk", "wetalk-plus", "wetalk-coach" ] as const;
    const availableCodes = enterpriseSlugs.filter((slug) => !!this.catalogBySlug[slug]);

    return (availableCodes.length ? availableCodes : enterpriseSlugs) as OfferPlanCode[];
  }

  getPlanDisplayName(planCode: OfferPlanCode): string {
    return this.catalogBySlug[planCode]?.name
      ?? SubscriptionPlanMetadata[planCode]?.name
      ?? planCode;
  }

  resolveCatalogPlanVersion(
    planCode: OfferPlanCode | undefined,
    plan?: SubscriptionPlanCatalogItem,
    latestVersion?: SubscriptionPlanVersion,
  ): SubscriptionPlanVersion | undefined {
    if (latestVersion) {
      return latestVersion;
    }

    const selectedPlan = plan ?? (planCode ? this.catalogBySlug[planCode] : undefined);

    return resolvePlanVersionForCatalogPricing(selectedPlan?.versions) as
      | SubscriptionPlanVersion
      | undefined;
  }

  getLatestVersionForPlan(
    planCode: OfferPlanCode | undefined,
    latestVersion?: SubscriptionPlanVersion,
    plan?: SubscriptionPlanCatalogItem,
  ): SubscriptionPlanVersion | undefined {
    return this.resolveCatalogPlanVersion(planCode, plan, latestVersion);
  }

  getAnnualPricePerSeatCents(
    planCode: OfferPlanCode | undefined,
    seatCount: number,
    latestVersion?: SubscriptionPlanVersion,
  ): number {
    const version = this.getLatestVersionForPlan(planCode, latestVersion);

    return getAnnualPricePerSeatCentsFromCapabilities(version?.rulesJson, seatCount);
  }

  getIncludedJourneysCount(
    latestVersion: SubscriptionPlanVersion | undefined,
    seatCount: number,
    organization?: Organization,
  ): number {
    const includedCreditsPerSeat =
      latestVersion?.rulesJson?.credit_policy?.included_cycle_credits_per_collaborator
      ?? 0;
    const journeyCreditsCost =
      latestVersion?.rulesJson?.credit_policy?.journey_credit_cost
      ?? organization?.creditSummary?.journeyCreditsCost
      ?? 10;

    return Math.floor((includedCreditsPerSeat * seatCount) / Math.max(1, journeyCreditsCost));
  }

  getComplementaryJourneyPriceCents(
    latestVersion: SubscriptionPlanVersion | undefined,
    organization?: Organization,
  ): number {
    const commercialTerms = latestVersion?.rulesJson?.credit_policy?.commercial_terms;

    return commercialTerms?.additional_journey_price_cents
      ?? commercialTerms?.journey_price_cents
      ?? organization?.capabilities?.credit_policy?.commercial_terms?.additional_journey_price_cents
      ?? organization?.capabilities?.credit_policy?.commercial_terms?.journey_price_cents
      ?? 0;
  }

  getCurrentComplementaryJourneyPriceCents(organization?: Organization): number {
    const commercialTerms = organization?.capabilities?.credit_policy?.commercial_terms;
    return commercialTerms?.additional_journey_price_cents
      ?? commercialTerms?.journey_price_cents
      ?? 0;
  }

  getTodayDate(): string {
    return new Date().toISOString().split("T")[0];
  }

  getNextRenewalDate(startsAt: string): Date {
    const date = new Date(startsAt);
    date.setFullYear(date.getFullYear() + 1);
    return date;
  }

  formatCurrency(amountCents: number): string {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amountCents / 100);
  }

  normalizePositiveNumber(value: number | string | null | undefined, fallback: number): number {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  }
}
